import React, { useEffect, useRef, useState } from 'react';
import { Card, Input, message, Spin, Typography } from 'antd';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import { useMutation, useQuery } from '@tanstack/react-query';

import { TRADEBOOK } from '../../../../utilities/functions';

import CustomSubsections from './CustomSubsections';
import StandardFields from './StandardFields';

import './styles.scss';

const { Title } = Typography;

export default function TradingPlanEditor({ planId }) {
    // Fetch plan data
    const { isLoading, data: plan } = useQuery(
        ['tradebook-plan', planId],
        () => TRADEBOOK.fetchTradingPlan(planId),
        { enabled: !!planId, retry: false }
    );

    const [localPlan, setLocalPlan] = useState(null);
    const previousPlanIdRef = useRef(null);

    useEffect(() => {
        if (plan && planId !== previousPlanIdRef.current) {
            setLocalPlan(plan);
            previousPlanIdRef.current = planId;
        }
    }, [plan, planId]);

    // Update plan mutation
    const updatePlanMutation = useMutation(
        ({ planId: id, updates }) => TRADEBOOK.updateTradingPlan(id, updates),
        {
            onError: (error) => {
                message.error('Failed to save changes');
                console.error('Update error:', error);
            }
        }
    );

    // Debounced autosave
    const debouncedSave = useRef(
        debounce((id, updates) => {
            updatePlanMutation.mutate({ planId: id, updates });
        }, 1000)
    ).current;

    const handleFieldChange = (field, value) => {
        setLocalPlan((prev) => ({ ...prev, [field]: value }));
        debouncedSave(planId, { [field]: value });
    };

    const handleCustomSubsectionsChange = (customSubsections) => {
        setLocalPlan((prev) => ({ ...prev, customSubsections }));
        debouncedSave(planId, { customSubsections });
    };

    if (isLoading || !localPlan) {
        return (
            <div className="trading-plan-editor-loading">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="trading-plan-editor">
            <Card title={<Title level={3}>Trading Plan: {localPlan.strategyName}</Title>}>
                {/* Strategy Name */}
                <div className="field-group">
                    <label>Strategy Name</label>
                    <Input
                        value={localPlan.strategyName}
                        onChange={(e) => handleFieldChange('strategyName', e.target.value)}
                        placeholder="Enter strategy name"
                        size="large"
                    />
                </div>

                {/* Standard Fields */}
                <StandardFields
                    plan={localPlan}
                    planId={planId}
                    onFieldChange={handleFieldChange}
                />

                {/* Custom Subsections */}
                <CustomSubsections
                    customSubsections={localPlan.customSubsections || []}
                    onChange={handleCustomSubsectionsChange}
                />
            </Card>
        </div>
    );
}

TradingPlanEditor.propTypes = {
    planId: PropTypes.string
};
